import { SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Model } from '@df/catalog/model';
import { ImageModule } from '@df/module-image/image.module';
import { KlevuSearchService } from '@df/module-klevu-search/klevu-search.service';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { Product } from '../../catalog/product/product.decorator';
import { ProductComponent } from '../../catalog/product/product/product.component';
import { ShellComponent } from '../../ui/display/local-shell.component';
export interface ISearchTrendingContentBlock {
    header: string;
    items: {
        image: string;
        title: string;
        link: string;
        copy: string;
        cta_copy: string;
        disableKlevu: boolean;
    }[];
}
export interface ISearchTrendingContentBlockData extends Catalog.IBlockData<ISearchTrendingContentBlock> {}
export enum ISearchTrendingMode {
    icms = 'icms',
    klevu = 'klevu'
}
@Component({
    selector: 'search-trending',
    templateUrl: 'search-trending.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [ImageModule, ShellComponent, HrefComponent, ProductComponent, SlicePipe, GridModule, ICmsClientModule]
})
@ICmsComponent<SearchTrendingComponent>({
    id: 'search-trending',
    block: {
        url: 'search-trending'
    },
    onDemandOnly: true,
    name: 'Search Trending Content Block',
    description: 'Content block used to display trending search',
    fields: [
        <NICmsContentField.IField>{
            key: 'header',
            type: NICmsContentField.EFieldType.text,
            title: 'Header',
            required: true
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.toggle,
            key: 'disableKlevu',
            title: 'Disable Klevu',
            description:
                'By default this block shows Klevu trending products, and iCMS content filled below is used as fallback. Tick this checkbox to always show iCMS content instead of Klevu trending products.',
            default: false
        },
        <NICmsContentField.IRepeaterField>{
            key: 'items',
            type: NICmsContentField.EFieldType.repeater,
            title: 'Items',
            itemName: 'Item',
            itemNameField: 'title',
            maxSize: 4,
            fields: [
                <NICmsContentField.IField>{
                    key: 'image',
                    type: NICmsContentField.EFieldType.image,
                    title: 'Image',
                    required: true
                },
                <NICmsContentField.IField>{
                    key: 'title',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Title',
                    required: true
                },
                <NICmsContentField.IField>{
                    key: 'link',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Link',
                    required: true
                },
                <NICmsContentField.IField>{
                    key: 'copy',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Copy',
                    required: true
                },
                <NICmsContentField.IField>{
                    key: 'cta_copy',
                    type: NICmsContentField.EFieldType.text,
                    title: 'CTA copy',
                    required: true,
                    default: 'Read More'
                }
            ],
            default: []
        }
    ]
})
export class SearchTrendingComponent extends UiComponent implements NICmsContentComponent.IComponent, OnChanges, OnInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: ISearchTrendingContentBlock;
    mode?: ISearchTrendingMode;
    klevuTrendingProducts?: Product[];
    /**
     * Klevu search service
     */

    protected klevuSearchService = inject(KlevuSearchService);
    /**
     * Tracking function
     */
    trackByIdFn(index: number, model: Model): number | string {
        return model.id;
    }
    override ngOnInit() {
        this.update();
        super.ngOnInit();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['content']) {
            this.update();
        }
        super.ngOnChanges(changes);
    }

    update(): void {
        // Set mode to iCMS if Klevu disabled
        if ((<any>this.content)?.disableKlevu) {
            this.setMode(ISearchTrendingMode.icms);
        } else {
            const promise = this.klevuSearchService.getKlevuTrendingProductsPromise();
            promise
                .then(result => {
                    const products = result.products as Product[];
                    // Set mode to Klevu if we have enough products to show;
                    if ((products?.length || 0) >= 4) {
                        this.klevuTrendingProducts = products;
                        this.setMode(ISearchTrendingMode.klevu);
                    } else {
                        this.setMode(ISearchTrendingMode.icms);
                    }
                })
                .catch(() => this.setMode(ISearchTrendingMode.icms));
        }
    }

    private setMode(mode: ISearchTrendingMode): void {
        if (this.mode !== mode) {
            this.mode = mode;
            this.detectChanges();
        }
    }
}
